import api, { IPaginationResponse } from 'config/axios';
import AppService from 'common/app-service';

export type BeneficiaryType = 'PERSON' | 'COMPANY';
export type PixKeyType = 'CPF' | 'CNPJ' | 'EMAIL' | 'PHONE' | 'EVP';
export type BankAccountType = 'CHECKING' | 'SAVINGS' | 'PAYMENT';
export type EarlyPaymentConditionType = 'FIXED' | 'PERCENTAGE';
export type CounterpartyType = 'FUNDS_TRANSFER' | 'BOLETO_ISSUANCE';

export interface CreateBeneficiaryDTO {
  businessName?: string;
  tradingName?: string;
  beneficiaryType: BeneficiaryType;
  counterpartyType?: CounterpartyType;
  taxNumber?: string;
  category?: string;

  // PIX
  pixName?: string;
  pixKey?: string;
  pixKeyType?: PixKeyType;

  // Bank
  bankCode?: string;
  bankBranch?: string;
  bankAccount?: string;
  bankAccountDigit?: string;
  bankAccountType?: BankAccountType;

  // Address
  postalCode?: string;
  street?: string;
  streetNumber?: string;
  addressComplement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;

  // Conditions
  allowLatePayment?: boolean;
  lateFeePercentage?: number;
  latePaymentInterestRate?: number;
  latePaymentGraceDays?: number;

  earlyPaymentDiscountEnabled?: boolean;
  earlyPaymentConditionType?: EarlyPaymentConditionType;
  earlyDiscountValue?: number;
  earlyPaymentDiscountDays?: number;
}

export interface BeneficiaryView {
  id: number;
  businessName?: string | null;
  tradingName?: string | null;
  pixName?: string | null;
  beneficiaryType: BeneficiaryType;
  taxNumber?: string | null;
  category?: string;

  pixKey?: string | null;
  pixKeyType?: PixKeyType | null;

  bankCode?: string | null;
  branch?: string | null;
  accountNumber?: string | null;
  accountDigit?: string | null;
  accountType?: BankAccountType | null;

  postalCode?: string | null;
  street?: string | null;
  streetNumber?: string | null;
  addressComplement?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;

  allowLatePayment?: boolean | null;
  lateFeePercentage?: number | null;
  latePaymentInterestRate?: number | null;
  latePaymentGraceDays?: number | null;
  earlyPaymentDiscountEnabled?: boolean | null;
  earlyPaymentConditionType?: EarlyPaymentConditionType | null;
  earlyDiscountValue?: number | null;
  earlyPaymentDiscountDays?: number | null;

  isActive: boolean;
  createdAt: string;
  updatedAt?: string | null;
}

export interface BeneficiaryFilter {
  beneficiaryType?: BeneficiaryType;
  taxNumber?: string;
  businessName?: string;
  tradingName?: string;
  pixName?: string;
  pixKeyType?: PixKeyType;
  isActive?: boolean;
  counterpartyType?: CounterpartyType;
  fullSearch?: string;
}

export enum EBeneficiaryMode {
  ACCOUNT_HOLDER = 'ACCOUNT_HOLDER',
  OTHER = 'OTHER',
}

export default class BeneficiariesService extends AppService {
  constructor() {
    super('banking/beneficiaries', api);
  }

  async create(payload: CreateBeneficiaryDTO): Promise<BeneficiaryView> {
    const { data } = await this.api.post(this.root(), payload);
    return data;
  }

  async getById(beneficiaryId: number): Promise<BeneficiaryView> {
    const { data } = await this.api.get(this.root(beneficiaryId));
    return data;
  }

  async list(
    page = 1,
    limit = 10,
    mode: EBeneficiaryMode,
    filters: Partial<BeneficiaryFilter> = {}
  ): Promise<IPaginationResponse<BeneficiaryView>> {
    const { data } = await this.api.get(this.root(), {
      params: {
        page,
        limit,
        mode,
        ...filters,
      },
    });

    return data;
  }

  async update(beneficiaryId: number, payload: CreateBeneficiaryDTO): Promise<BeneficiaryView> {
    const { data } = await this.api.put(this.root(beneficiaryId), payload);
    return data;
  }

  async deactivate(beneficiaryId: number): Promise<void> {
    await this.api.delete(this.root(beneficiaryId));
  }
}
